import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import API_BASE_URL from '../config';
import Toast from '../components/common/Toast';
import './OrdersPage.css';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [toast, setToast] = useState(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/assets/product-placeholder.jpg';
    if (imagePath.startsWith('http')) return imagePath;
    return imagePath.replace('/assets/assets/', '/assets/');
  };

  // Resolve prescription image URL to full URL
  const getPrescriptionImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return 'https://visioncareoptics.in/frontend/public' + imageUrl;
  };

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    if (!isAuthenticated) { 
      navigate('/login', { state: { from: '/orders', message: 'Please login to view your orders' } }); 
      return; 
    }
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/orders/my`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) { 
        const data = await response.json(); 
        setOrders(data.data || []); 
      }
      else { 
        setOrders([]); 
      }
    } catch (error) {
      showToast('Error loading orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = { 
      pending: '#f39c12', 
      confirmed: '#2ecc71',
      processing: '#3498db', 
      shipped: '#9b59b6', 
      delivered: '#27ae60', 
      cancelled: '#e74c3c' 
    };
    const displayStatus = status === 'confirmed' ? 'Confirmed' : status;
    return <span className="status-badge" style={{ backgroundColor: colors[status] || '#95a5a6' }}>{displayStatus}</span>;
  };

  const getPaymentStatusBadge = (status) => {
    const colors = {
      pending: '#f39c12',
      advance_paid: '#e67e22',
      completed: '#27ae60',
      failed: '#e74c3c',
      refunded: '#9b59b6'
    };
    const displayText = status === 'advance_paid' ? 'Advance Paid' : (status || 'pending');
    return <span className="payment-status-badge" style={{ backgroundColor: colors[status] || '#95a5a6' }}>{displayText}</span>;
  };

  const getPaymentMethodIcon = (method, percentage = 50) => {
    if (method === 'cod') {
      return { icon: '💰', text: 'Cash on Delivery', class: 'method-cod' };
    } else if (method === 'razorpay') {
      return { icon: '💳', text: 'Full Online Payment', class: 'method-razorpay' };
    } else if (method === 'half_online_half_cod') {
      return { icon: '💸', text: `${percentage}% Online + ${100 - percentage}% COD`, class: 'method-hybrid' };
    }
    return { icon: '❓', text: method || 'Unknown', class: 'method-unknown' };
  };

  const formatPrice = (price) => `₹${parseFloat(price).toFixed(2)}`;

  // Render prescription
  const renderPrescriptionDetails = (prescriptionData) => {
    if (!prescriptionData) return null;

    if (prescriptionData.type === 'image') {
      const imgUrl = getPrescriptionImageUrl(prescriptionData.full_url || prescriptionData.image_url);
      return (
        <div className="order-prescription">
          <h4>📷 Prescription Image</h4>
          <div className="prescription-image-view">
            {imgUrl && (
              <>
                <img src={imgUrl} alt="Prescription" className="rx-order-img" onError={(e) => { e.target.style.display = 'none'; }} />
                <a href={imgUrl} target="_blank" rel="noopener noreferrer" className="rx-view-full">🔗 View Full Image</a>
              </>
            )}
          </div>
        </div>
      );
    }

    if (prescriptionData.right || prescriptionData.left) {
      return (
        <div className="order-prescription">
          <h4>📋 Prescription Details</h4>
          <div className="prescription-details">
            {['right', 'left'].map(eye => (
              <div key={eye} className="eye-details">
                <h5>{eye === 'right' ? 'Right Eye (OD)' : 'Left Eye (OS)'}</h5>
                <p>SPH: {prescriptionData[eye]?.sph  || '-'}</p>
                <p>CYL: {prescriptionData[eye]?.cyl  || '-'}</p>
                <p>AXIS: {prescriptionData[eye]?.axis || '-'}</p>
                <p>ADD: {prescriptionData[eye]?.add  || '-'}</p>
                <p>PD: {prescriptionData[eye]?.pd   || '-'}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) { 
    return (
      <div className="orders-page">
        <div className="container">
          <div className="loading-spinner">Loading your orders...</div>
        </div>
      </div>
    ); 
  }

  return (
    <div className="orders-page">
      <div className="container">
        <h1 className="page-title">My Orders</h1>

        {orders.length === 0 ? (
          <div className="no-orders">
            <h3>No orders yet</h3>
            <Link to="/products" className="btn-primary">Start Shopping</Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(order => {
              const paymentMethod = getPaymentMethodIcon(order.payment_method, order.payment_percentage);
              const isHybridPayment = order.payment_method === 'half_online_half_cod';
              
              return (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div className="order-info">
                      <span className="order-number">Order #{order.order_number}</span>
                      <span className="order-date">{order.created_at}</span>
                    </div>
                    <div className="order-status">{getStatusBadge(order.order_status)}</div>
                  </div>

                  <div className="order-body">
                    <div className="order-items">
                      {order.items && order.items.map((item, index) => (
                        <div key={index} className="order-item">
                          <div className="item-image">
                            <img 
                              src={getImageUrl(item.image)} 
                              alt={item.product_name} 
                              onError={(e) => e.target.src = '/assets/product-placeholder.jpg'} 
                            />
                          </div>
                          <div className="item-details">
                            <h4>{item.product_name}</h4>
                            {item.variant_name    && <p>Color: {item.variant_name}</p>}
                            {item.size_name       && <p>Size: {item.size_name}</p>}
                            {item.lens_name       && <p>Lens: {item.lens_name}{item.lens_range_name && ` (${item.lens_range_name})`}</p>}
                            {item.lens_color_name && <p>Lens Color: {item.lens_color_name}</p>}
                            {item.prescription?.type === 'image' && <p className="rx-note">📷 Prescription image uploaded</p>}
                            {item.prescription && item.prescription.type !== 'image' && item.prescription.right?.sph && (
                              <p className="rx-note">Rx: R {item.prescription.right.sph} | L {item.prescription.left?.sph || '-'}</p>
                            )}
                            <p>Qty: {item.quantity} × {formatPrice(item.price)}</p>
                          </div>
                          <div className="item-total">{formatPrice(item.price * item.quantity)}</div>
                        </div>
                      ))}
                    </div>

                    <div className="order-summary-grid">
                      <div className="order-shipping">
                        <h4>📦 Shipping Address</h4>
                        <p>{order.shipping_address}</p>
                      </div>

                      <div className="order-payment-info">
                        <h4>💳 Payment Information</h4>
                        <div className="payment-details">
                          <div className="payment-row">
                            <span>Method:</span>
                            <strong className={paymentMethod.class}>
                              {paymentMethod.icon} {paymentMethod.text}
                            </strong>
                          </div>
                          <div className="payment-row">
                            <span>Status:</span>
                            {getPaymentStatusBadge(order.payment_status || 'pending')}
                          </div>
                          
                          {isHybridPayment && (
                            <>
                              <div className="payment-divider"></div>
                              <div className="payment-row hybrid-breakdown">
                                <span>Advance Paid (Online):</span>
                                <strong className="advance-amount">
                                  {formatPrice(order.advance_amount || (order.total_amount * 0.5))}
                                </strong>
                              </div>
                              <div className="payment-row hybrid-breakdown">
                                <span>Remaining (On Delivery):</span>
                                <strong className="cod-amount">
                                  {formatPrice(order.remaining_amount || (order.total_amount * 0.5))}
                                </strong>
                              </div>
                              {order.cod_paid_at && (
                                <div className="payment-row success-note">
                                  <span>✅ COD Collected:</span>
                                  <small>{new Date(order.cod_paid_at).toLocaleString()}</small>
                                </div>
                              )}
                              {order.payment_status === 'advance_paid' && order.order_status !== 'delivered' && (
                                <div className="payment-row info-note">
                                  <span>ℹ️ Note:</span>
                                  <small>Please pay remaining amount when you receive your order</small>
                                </div>
                              )}
                            </>
                          )}
                          
                          {order.payment_method === 'cod' && order.payment_status === 'pending' && (
                            <div className="payment-row info-note">
                              <span>ℹ️ Note:</span>
                              <small>Please keep exact cash ready for delivery</small>
                            </div>
                          )}
                          
                          {order.payment_method === 'razorpay' && order.payment_status === 'completed' && (
                            <div className="payment-row success-note">
                              <span>✅ Payment Completed:</span>
                              <small>Full amount paid online</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded prescription details */}
                    {expandedOrderId === order.id && renderPrescriptionDetails(order.prescription_data)}
                  </div>

                  <div className="order-footer">
                    <div className="order-total">
                      <span>Total Amount:</span>
                      <span className="total-amount">{formatPrice(order.total_amount)}</span>
                    </div>
                    <button 
                      className="view-details-btn" 
                      onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                    >
                      {expandedOrderId === order.id ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default OrdersPage;